import { Base } from '@studiometa/js-toolkit';
import { createApp } from 'vue';
import FloatingVue from 'floating-vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import VueSidePanel from 'vue3-side-panel';
import { Collapse } from 'vue-collapsed';
import { format, addDays } from 'date-fns';
import VueSearchPrice from './SearchPrice.vue';
import { wpLocalizedApi } from '../../utils/index.js';

import 'floating-vue/dist/style.css';
import '@vuepic/vue-datepicker/dist/main.css';
import 'vue3-side-panel/dist/vue3-side-panel.css';

/**
 * @typedef {{
 *   $options: {
 *     id: number;
 *     bannerInfo: string;
 *   }
 * }} SearchPriceProps
 */

/**
 * SearchPrice component
 * @augments {Base<SearchPriceProps>}
 */
export default class SearchPrice extends Base {
  /**
   * Config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'SearchPrice',
    options: {
      // Post id
      id: Number,
      bannerInfo: String,
    },
  };

  /**
   * Get initial post data
   */
  async getInitialProps() {
    const searchParams = new URLSearchParams(decodeURIComponent(window.location.search));
    const params = new URLSearchParams({
      id: String(this.$options.id),
      from: searchParams.get('from') ?? format(addDays(new Date(), 2), 'yyyy-MM-dd'),
      to: searchParams.get('to') ?? format(addDays(new Date(), 4), 'yyyy-MM-dd'),
      adult: searchParams.get('adult') ?? '2',
      child: searchParams.get('child') ?? '0',
      babies: searchParams.get('babies') ?? '0',
      dog: searchParams.get('dog') ?? '0',
    });
    const tinyhouseEndpoint = new URL('/wp-json/custom/tinyhouse', window.location.origin);
    tinyhouseEndpoint.search = params.toString();

    const [trad, item] = await Promise.all([
      wpLocalizedApi(`/wp-json/custom/weglot`),
      wpLocalizedApi(tinyhouseEndpoint, { method: 'POST' }),
    ]);

    this.mount({
      id: this.$options.id,
      from: new Date(params.get('from')),
      to: new Date(params.get('to')),
      bannerInfo: this.$options.bannerInfo,
      post: item,
      giftUrl: item.gift_url,
      trad,
    });
  }

  /**
   * Mount the vue app.
   * @param {Record<string, unknown>} props
   */
  mount(props) {
    const app = createApp(VueSearchPrice, props);
    app.use(FloatingVue);
    app.use(VueSidePanel);
    app.component('VueDatePicker', VueDatePicker);
    app.component('Collapse', Collapse);

    app.mount(this.$el);
  }

  /**
   * Instantiate the Vue app.
   */
  mounted() {
    this.getInitialProps();
  }
}
